import MomentUtils from "@date-io/moment";
import { Dialog, DialogContent, makeStyles } from "@material-ui/core";
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { debounce } from "lodash";
import moment from "moment";
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { BiPlusCircle } from "react-icons/bi";
import { FaRegTrashAlt } from "react-icons/fa";
import { LuChevronDown, LuSparkles } from "react-icons/lu";
import {
  MdClose,
  MdLink,
  MdOutlineFileUpload,
  MdPeople,
  MdPictureAsPdf,
  MdWarning,
} from "react-icons/md";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { handleKeyDown } from "../../../common/common";
import { saveGuideProgress } from "../../../common/guides";
import CargoPdf from "../../../components/Calculator/CargoPdf";
import CounterPartySelection from "../../../components/Common/CounterPartySelection";
import CustomShowModal from "../../../components/Common/CustomShowModal";
import DownloadPDF from "../../../components/Common/DowloadPDF";
import UploadPDFModal from "../../../components/Common/UploadPDF";
import CreateWithAIModal from "../../../components/Common/CreateWithAIModal";
import { validateAIImportCargo } from "../../../util/aiImport/aiImportValidator";
import { applyValidCargo } from "../../../util/aiImport/aiImportMapper";
import AddPortQueryModal from "../../../components/Orders/AddPortQueryModal";
import {
  addQueryInitialData,
  queryPortDisabledFields,
  refreshCanalOptions,
  termsCoEfficientTable,
} from "../../../constants";
import DownloadAIInstructionPdf from "../../../util/aiImport/DownloadAIInstructionPdf";
import { getCurrentUserOrganizationId, toastSuccess } from "../../../utils";
import * as commonActions from "../../Common/actions";
import { updateEstimateCargo } from "../Calculator/actions";
import * as actions from "./actions";
import { validateNewQuery } from "./addQueryValidation";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      borderRadius: 0,
      minWidth: "400px",
    },
  },
  dialogTitle: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(1),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.common.white,
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    padding: theme.spacing(2),
  },
  linkList: {
    width: "100%",
    maxHeight: "400px",
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
  },
  linkItem: {
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const CARGO_AI_EXAMPLE = `25,000 MT (+/-10%) milling wheat, load Novorossiysk, discharge Alexandria.
Laycan 5-10 Nov 2026. Freight USD 28 pmt, brokerage 1.25%, address commission 3.75%.
Load rate 5000 MT/day SHEX-24hrs, discharge rate 4000 MT/day SHINC.
Counter party: Agri Trade Ltd. Via Suez is fine.`;

export const buildCargoPdfDetails = (query, portOperations) => {
  const data = portOperations?.map((portDetails) => ({
    operation: portDetails.operation,
    lat: portDetails?.lat,
    lng: portDetails?.lng,
    port: portDetails?.port,
    cargoQty: portDetails?.cargoQty?.toString(),
    tolerance: portDetails?.tolerance?.toString(),
    rate: portDetails?.rate?.toString(),
    terms: portDetails?.terms,
    turnTime: portDetails?.turnTime?.toString(),
    freight: portDetails?.freight,
    min: portDetails?.min?.toString(),
    max: portDetails?.max?.toString(),
    adcom: portDetails?.adcom?.toString(),
    other: portDetails?.other?.toString(),
    brokerage: portDetails?.brokerage?.toString(),
    account: portDetails?.account,
    cargo: portDetails?.cargo,
    stowFactor: portDetails?.stowFactor?.toString(),
  }));
  const tabledata = data?.filter(
    (field) =>
      field?.operation === "Loading" || field?.operation === "Discharging",
  );

  const querydata = {
    queryid: query?._id,
    name: query?.name,
    freightMode: query?.freightMode,
    qtyMode: query?.qtyMode,
    access: query?.access,
    laycanStartDate: query?.laycanStartDate,
    laycanEndDate: query?.laycanEndDate,
    message: query?.message,
    portOperations: tabledata,
    panama: query?.panama,
    suez: query?.suez,
    Tag1: query?.Tag1,
    Tag2: query?.Tag2,
    Tag3: query?.Tag3,
    Tag4: query?.Tag4,
  };
  return querydata;
};

export function CargoAIModal({ open, handleClose, query = addQueryInitialData, portOperations, setQuery, setPortOperations, onFilled }) {
  return (
    <CreateWithAIModal
      open={open}
      handleClose={handleClose}
      title="Create Cargo with AI"
      filledLabel="Cargo details filled"
      tips="Works best when it includes: cargo and quantity, load and discharge ports, laycan dates, freight, rates and terms."
      example={CARGO_AI_EXAMPLE}
      onParsed={async (cargo, onStatus) => {
        const validation = validateAIImportCargo(cargo);
        if (!validation.isValid) throw new Error(validation.error);
        const unresolved = await applyValidCargo(validation.data, { query, portOperations, setQuery, setPortOperations, onStatus });
        onFilled?.();
        return unresolved > 0 ? `${unresolved} port(s) need manual confirmation.` : "";
      }}
    />
  );
}

const AddNewOrder = forwardRef((props, ref) => {
  const {
    setLoader = () => { },
    onCloseModal = () => { },
    loader,
    selectedQuery = {},
    selectedPorts = [],
    selectedPortsCommence = [],
    isEdit = false,
    isFromSingleVesselCalcy = false,
    getInitialData = () => { },
    onShareOrgClick = () => { },
    tabvalue,
    isContractEstimate = false,
    handleTourAction = () => { },
    handleActionComplete = () => { },
    handleActionError = () => { },
    disableUploadPdf = false,
    hideCreateAI = false,
    isCounterPartySelection = false,
  } = props;
  const organizationId = getCurrentUserOrganizationId();

  const addPortInitialData = [
    {
      _id: uuidv4(),
      cargoId: "",
      operation: "Loading",
      port: "",
      cargoQty: 0,
      tolerance: 0,
      rate: 0,
      terms: "SHINC",
      turnTime: 0,
      pdaRemarks: "",
      grade: "",
      freight: 0,
      adcom: 0,
      other: 0,
      brokerage: 0,
      ttlComm: 0,
      netFrt: 0,
      min: 0,
      max: 0,
      account: "",
      cargo: "",
      factor: 1,
      stowFactor: 0,
      queryId: "",
      loadline: "S-Summer",
      sulfur: "Normal",
    },
    {
      _id: uuidv4(),
      cargoId: "",
      operation: "Discharging",
      port: "",
      cargoQty: 0,
      tolerance: 0,
      rate: 0,
      terms: "SHINC",
      turnTime: 0,
      pdaRemarks: "",
      grade: "",
      freight: 0,
      adcom: 0,
      other: 0,
      brokerage: 0,
      ttlComm: 0,
      netFrt: 0,
      min: 0,
      max: 0,
      account: "",
      cargo: "",
      factor: 1,
      stowFactor: 0,
      queryId: "",
      loadline: "S-Summer",
      sulfur: "Normal",
    },
  ];

  const dispatch = useDispatch();

  const [portOperations, setPortOperations] = useState([...addPortInitialData]);
  const [query, setQuery] = useState({ ...addQueryInitialData });
  const [portModal, setPortModal] = useState(false);
  const [selectedPort, setSelectedPort] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [getOpenUploadPDF, setOpenUploadPDF] = useState(false);
  const [getOpenCreateAI, setOpenCreateAI] = useState(false);
  const [pdfSaveId, setPdfSaveId] = useState("");
  const [TempCargoDetails, setTempCargoDetails] = useState({});
  const [AttachmentFiles, setAttachmentFiles] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [TagsOptions, setTagsOptions] = useState([]);
  const { id = "" } = useParams();
  const fileInputRef = useRef(null);

  const selectedCargoDetails = () => {
    setTempCargoDetails(buildCargoPdfDetails(query, portOperations));
  };

  useEffect(() => {
    selectedCargoDetails();
  }, [portOperations, query]);

  useEffect(() => {
    setPdfSaveId(uuidv4());
    if (handleActionComplete) {
      handleActionComplete("create");
    }
  }, []);

  useEffect(() => {
    if (Object.keys(selectedQuery).length) {
      setQuery(selectedQuery);
    }
    if (Array.isArray(selectedPorts) && selectedPorts.length) {
      setPortOperations(selectedPorts);
    }
    return () => {
      resetStateVariables();
    };
  }, [selectedQuery, selectedPorts]);

  useEffect(() => {
    const { errors } = validateNewQuery(query, portOperations);
    dispatch(commonActions.addOnlyErrors({ errorList: errors }));
  }, [query, portOperations]);

  const resetStateVariables = () => {
    setPortOperations([...addPortInitialData]);
    setQuery({ ...addQueryInitialData });
    setPortModal(false);
    setSelectedPort({});
    setSelectedIndex(0);
  };

  const onOpenErrorAlert = (errors = []) => {
    dispatch(commonActions.openErrorAlert({ errorList: errors }));
  };

  const debouncedSearch = useCallback(
    debounce((search, type, name) => {
      onSearchTagsList(search, type, name);
    }, 400),
    [],
  );

  const onSearchTagsList = (value, type, name) => {
    if (value) {
      setTagsLoading(name);
      actions
        .tagsByCharAPI(value, type)
        .then((res) => {
          setTagsLoading(false);
          let tagsList = [];
          if (res.data.list?.length > 0) {
            if (type === "individual") {
              tagsList = res.data.list.map((item) => ({
                label: `${item.individual_firstName} (${item?.individual_emailPersonal})`,
                value: item._id,
                name: item.individual_firstName,
              }));
            } else {
              tagsList = res.data.list.map((item) => ({
                label: `${item.organisation_fullName} (${item?.organisation_emailOfficial})`,
                value: item._id,
                name: item.organisation_fullName,
              }));
            }
          }
          setTagsOptions(tagsList);
        })
        .catch((err) => {
          setTagsLoading(false);
          setTagsOptions([]);
        });
    }
  };

  const delayTagsSearch = (search, type, name) => {
    debouncedSearch(search, type, name);
  };

  useImperativeHandle(ref, () => {
    return {
      getValue: () => {
        const { errors, isValid } = validateNewQuery(query, portOperations);
        if (!isValid || errors?.length) {
          onOpenErrorAlert(errors);
          return false;
        }
        return {
          ...query,
          cargoList: portOperations,
          laycanStartDate: moment(query.laycanStartDate).toISOString(),
          laycanEndDate: moment(query.laycanEndDate).toISOString(),
          organizationId,
        }
      },
      isSubmitting,
      async onSubmitQuery(isCopy = false, isShareName = "") {
        const finalPortOperations = [
          ...selectedPortsCommence,
          ...portOperations,
        ];
        const { errors, isValid } = validateNewQuery(query, portOperations);
        if (!isValid || errors?.length) {
          onOpenErrorAlert(errors);
          return false;
        }

        setIsSubmitting(true);
        setLoader(true, "Saving data...");

        const formData = new FormData();

        try {
          let newQuery = {
            ...query,
            laycanStartDate: moment(query.laycanStartDate).toISOString(),
            laycanEndDate: moment(query.laycanEndDate).toISOString(),
            cargoList: finalPortOperations,
            organizationId,
          };

          if (isCopy) {
            newQuery = { ...TempCargoDetails };
            const newPortOperation = [
              ...(TempCargoDetails?.portOperations || []),
            ];
            delete newQuery?.portOperations;

            if (isShareName) {
              newQuery.name = isShareName;
              newQuery.access = "public";
              newQuery.organizationId = organizationId;
            } else {
              newQuery.access = "private";
            }

            newQuery.cargoList = newPortOperation;
          } else {
            newQuery.cargoList = finalPortOperations;
          }

          isContractEstimate
            ? formData.append("data", JSON.stringify(newQuery) || "")
            : formData.append("QueryData", JSON.stringify(newQuery) || "");

          AttachmentFiles.forEach((file, index) => {
            formData.append("files", file);
          });

          if (isEdit && !isCopy) {
            try {
              if (isFromSingleVesselCalcy) {
                if (isContractEstimate) {
                  await updateEstimateCargo({ _id: id }, formData)
                    .then(() => {
                      toastSuccess("Query updated successfully");
                      handleActionComplete("save");
                      getInitialData(id);

                      setLoader(false);
                      setIsSubmitting(false);
                    })
                    .catch(() => {
                      handleActionError("save");
                      setLoader(false);
                      setIsSubmitting(false);
                      throw new Error("update failed");
                    });
                } else {
                  await actions
                    .updateClientQueryForSingleVessel(id, formData)
                    .then(() => {
                      toastSuccess("Query updated successfully");
                      handleActionComplete("save");
                      getInitialData(id);
                      Promise.all([
                        !!organizationId
                          ? dispatch(actions.clientQueryOrg())
                          : null,
                        dispatch(actions.clientQueryUser()),
                        dispatch(actions.getQueryFormatForUser()),
                      ]);

                      setLoader(false);
                      setIsSubmitting(false);
                    })
                    .catch(() => {
                      handleActionError("save");
                      setLoader(false);
                      setIsSubmitting(false);
                      throw new Error("update failed");
                    });
                }
              } else {
                await actions
                  .updateClientQuery(formData)
                  .then(() => {
                    toastSuccess("Query updated successfully");
                    handleActionComplete("save");
                    Promise.all([
                      !!organizationId
                        ? dispatch(actions.clientQueryOrg())
                        : null,
                      dispatch(actions.clientQueryUser()),
                      dispatch(actions.getQueryFormatForUser()),
                    ]);
                    setLoader(false);
                    setIsSubmitting(false);
                  })
                  .catch(() => {
                    handleActionError("save");
                    setLoader(false);
                    setIsSubmitting(false);
                    throw new Error("update failed");
                  });
              }
              setLoader(false);
            } catch (ex) {
              setLoader(false);
              setIsSubmitting(false);
              return false;
            }
            onCloseModal();
            return true;
          }

          await actions
            .addClientQuery(formData)
            .then(() => {
              toastSuccess("Query added successfully");
              saveGuideProgress("cargo", { created: true });
              handleActionComplete("created");
              onCloseModal();
              Promise.all([
                !!organizationId ? dispatch(actions.clientQueryOrg()) : null,
                dispatch(actions.clientQueryUser()),
                dispatch(actions.getQueryFormatForUser()),
              ]);
              setLoader(false);
              setIsSubmitting(false);
            })
            .catch(() => {
              handleActionError("save");
              setLoader(false);
              setIsSubmitting(false);
              throw new Error("add failed");
            });
          return true;
        } catch (ex) {
          setLoader(false);
          setIsSubmitting(false);
          console.error("Error in submitting query:", ex);
          return false;
        }
      },
    };
  });

  const onDownloadPdf = async () => {
    const result = await actions.addDownloadPdf(TempCargoDetails);
    return result;
  };

  const onChangeHandler = (e) => {
    const { value = "", name = "", type = "", checked } = e.target || {};
    const finalValue = type === "checkbox" ? checked : value;

    if (name === "freightMode") {
      if (value === "Single Freight") {
        const firstLoadingIndex = portOperations.findIndex(
          (item) => item?.operation?.toLowerCase() === "loading"
        );
        setPortOperations(
          portOperations.map((item, index) => {
            if (firstLoadingIndex !== -1 && index !== firstLoadingIndex && item.operation === "Loading") {
              return {
                ...item,
                freight: 0,
                adcom: 0,
                other: 0,
                brokerage: 0,
              };
            }
            return item;
          }),
        );
      }
    }
    setQuery({ ...query, [name]: finalValue });
  };

  const onChangePortOperation = (index, e) => {
    const { name = "", value = "", type = "" } = e.target;
    let updatedPortOperation = [...portOperations];
    updatedPortOperation[index][name] =
      type === "number" ? Number(value) : value;

    if (name === "operation" && value === "Discharging") {
      const disabledFields = queryPortDisabledFields?.[value] || {};
      Object.keys(disabledFields).forEach((field) => {
        if (disabledFields[field]) {
          if (["account", "cargo"].includes(field)) {
            updatedPortOperation[index][field] = "";
          } else {
            updatedPortOperation[index][field] = 0;
          }
        }
      });
    }

    if (name === "terms") {
      const findFactor = termsCoEfficientTable.find(
        (item) => item.label === value,
      );

      if (findFactor) {
        updatedPortOperation[index]["factor"] = findFactor.value;
      }
    }

    setPortOperations(updatedPortOperation);
  };

  const onAddPortOperations = (index) => {
    const newPortOperations = [...portOperations];
    newPortOperations.splice(index + 1, 0, { ...addPortInitialData[1] });
    setPortOperations(newPortOperations);
    handleActionComplete("add-ports");
  };

  const onDeletePortOperations = (index) => {
    let updatedOperation = [...portOperations];
    updatedOperation.splice(index, 1);
    setPortOperations(updatedOperation);
  };

  const onSelectPortsOfOperations = (portObj, index) => {
    const portQuery = portObj.needsValidation && portObj.port ? { port: portObj.port } : portObj;
    setSelectedPort(portQuery);
    setSelectedIndex(index);
    setPortModal(true);
  };

  const onSavePortOperationsOfQuery = (port, val) => {
    let updatedPortOperation = [...portOperations];
    updatedPortOperation[selectedIndex]["port"] = port;
    updatedPortOperation[selectedIndex]["lat"] = val?.lat;
    updatedPortOperation[selectedIndex]["lng"] = val?.lng;
    updatedPortOperation[selectedIndex]["needsValidation"] = false;

    setPortOperations(updatedPortOperation);
    onClosePortModal();
  };

  const onClosePortModal = () => {
    setPortModal(false);
    setSelectedPort({});
    setSelectedIndex(0);
  };

  const onChangeDate = (type, date) => {
    setQuery({ ...query, [type]: date });
  };

  const onSelectTags = (value, name) => {
    setQuery({ ...query, [name]: value });
    setTagsOptions([]);
  };

  const onChangeHandlerAccess = (e) => {
    const { checked = "", name = "" } = e.target || {};
    setQuery({ ...query, [name]: checked ? "public" : "private" });
  };

  const {
    access = "",
    freightMode = "",
    message = "",
    name = "",
    qtyMode = "",
    suez = false,
    panama = false,
    laycanStartDate = null,
    laycanEndDate = null,
    Tag1 = "",
    Tag2 = "",
    Tag3 = "",
    Tag4 = "",
    Attachments = [],
  } = query || {};

  let showMinMax = false;
  if (qtyMode === "MINMAX") showMinMax = true;

  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const mediaTypes = useMemo(() => {
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".webp",
      ".bmp",
      ".tiff",
    ];
    const videoExtensions = [
      ".mp4",
      ".avi",
      ".mov",
      ".wmv",
      ".flv",
      ".webm",
      ".mkv",
    ];
    const documentExtensions = [
      ".pdf",
      ".doc",
      ".docx",
      ".odt",
      ".xls",
      ".xlsx",
      ".csv",
      ".xlsm",
      ".ppt",
      ".pptx",
      ".odp",
      ".txt",
    ];
    const sortedMedia = {
      images: [],
      videos: [],
      documents: [],
    };

    Attachments.forEach((url) => {
      url = url?.url || url;
      const extension = url.substring(url.lastIndexOf(".")).toLowerCase();

      if (imageExtensions.includes(extension)) {
        sortedMedia.images.push(url);
      } else if (videoExtensions.includes(extension)) {
        sortedMedia.videos.push(url);
      } else if (documentExtensions.includes(extension)) {
        sortedMedia.documents.push(url);
      }
    });

    return sortedMedia;
  }, [Attachments]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setAttachmentFiles((prevFiles) => [...prevFiles, ...files]);
    handleActionComplete("upload-attachment");
  };

  const handleRemoveFile = (item) => {
    if (typeof item === "number") {
      setAttachmentFiles((prevFiles) => prevFiles.filter((_, i) => i !== item));
    } else if (typeof item === "string") {
      setQuery((prevData) => {
        const currentAttachments = prevData.Attachments || [];
        const indexToRemove = currentAttachments.findIndex(
          (att) => (att?.url || att) === item,
        );

        if (indexToRemove === -1) return prevData;

        const updatedAttachments = [...currentAttachments];
        updatedAttachments.splice(indexToRemove, 1);

        return {
          ...prevData,
          Attachments: updatedAttachments,
        };
      });
    }
  };

  const updateCounterPartyAtPort = (portId, newCounterParties) => {
    setPortOperations((prevState) =>
      prevState.map((port) =>
        port._id === portId
          ? { ...port, counterParties: newCounterParties }
          : port,
      ),
    );
  };

  const handleDemurrageChange = (portId, newValue) => {
    setPortOperations((prevState) =>
      prevState.map((port) =>
        port._id === portId ? { ...port, demurrage: newValue } : port,
      ),
    );
  };

  const handleDespatchChange = (portId, newValue) => {
    setPortOperations((prevState) =>
      prevState.map((port) =>
        port._id === portId ? { ...port, despatch: newValue } : port,
      ),
    );
  };

  // Table styling matching screenshot color palette (light gray header, pale yellow cells, charcoal text, amber title)
  const thClass = "px-2 py-1 text-left border-r border-b border-slate-300 text-[11px] font-medium text-slate-800 bg-slate-100 capitalize tracking-wider align-middle overflow-hidden text-ellipsis";
  const tdClass = "border border-slate-300 p-0 align-middle bg-white";
  const inputClass = "w-full h-[22px] text-[11px] px-2 py-0 m-0 box-border block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 [&[type=number]]:text-right [&[type=number]]:tabular-nums disabled:bg-transparent disabled:text-slate-500 disabled:cursor-not-allowed rounded-none border-none placeholder:text-gray-400 font-medium text-left";

  return (
    <Fragment>
      <div className="w-full bg-white flex flex-col font-sans">
        <div className="flex flex-wrap justify-between items-center gap-1.5 mb-2">
          <h3 className="text-[11px] font-medium text-slate-900 capitalize tracking-wider leading-none py-0.5 border-l-4 border-l-[#313C4C] pl-2 m-0 whitespace-nowrap">
            Cargo Operations
          </h3>

          <div className="flex flex-wrap items-center gap-1">
            {!isFromSingleVesselCalcy &&
              !disableUploadPdf &&
              (isEdit ? (
                <DownloadPDF
                  pdfName={name}
                  onDownloadPdf={onDownloadPdf}
                  portOperations={portOperations}
                  query={query}
                  pdfDocument={
                    <CargoPdf
                      portOperations={portOperations}
                      query={query}
                      pdfSaveId={pdfSaveId}
                    />
                  }
                />
              ) : (
                <div className="flex flex-wrap items-center gap-1.5">
                  <button
                    data-spotlight="upload-cargo-pdf-btn"
                    className="flex items-center gap-1.5 px-2.5 py-1 h-[26px] text-[11px] font-medium text-white bg-[#313C4C] border border-[#313C4C] rounded-none shadow-2xs cursor-pointer hover:bg-[#0c2f50] transition-colors whitespace-nowrap"
                    onClick={() => {
                      setOpenUploadPDF(true);
                      handleTourAction("upload-pdf");
                    }}
                  >
                    <MdPictureAsPdf size={14} /> Upload PDF or CSV
                  </button>
                  {!hideCreateAI && (
                  <button
                    data-spotlight="create-ai-btn"
                    className="flex items-center gap-1.5 px-2.5 py-1 h-[26px] text-[11px] font-medium text-white bg-[#313C4C] border border-[#313C4C] rounded-none shadow-2xs cursor-pointer hover:bg-[#0c2f50] transition-colors"
                    onClick={() => setOpenCreateAI(true)}
                  >
                    <LuSparkles size={14} /> Create with AI
                  </button>
                  )}
                  <DownloadAIInstructionPdf />
                </div>
              ))}

            {isEdit && tabvalue === 0 && (
              <button
                className="flex items-center gap-1.5 px-2.5 py-1 h-[26px] text-[11px] font-medium text-slate-900 bg-white hover:bg-slate-50 border border-slate-300 rounded-none shadow-2xs transition-colors cursor-pointer"
                onClick={() => onShareOrgClick()}
              >
                <MdPeople size={14} className="text-[#313C4C]" />
                <span>Share with Team</span>
              </button>
            )}
          </div>
        </div>

        {/* Excel-like Strict Table Wrapper */}
        <div className="overflow-x-auto w-full mb-1.5">
          <table className="w-full text-left border-collapse border border-slate-300 whitespace-nowrap table-fixed">
            <thead>
              <tr>
                <th className={`${thClass} w-[26px] min-w-[26px]`}>Add</th>
                <th className={`${thClass} w-[80px]`}>Operation</th>
                <th className={`${thClass} w-[100px]`}>Port</th>
                {showMinMax ? (
                  <>
                    <th className={`${thClass} w-[60px]`}>Min</th>
                    <th className={`${thClass} w-[60px]`}>Max</th>
                  </>
                ) : (
                  <>
                    <th className={`${thClass} w-[80px]`}>Cargo Qty</th>
                    <th className={`${thClass} w-[60px]`}>Tol(%)</th>
                  </>
                )}
                <th className={`${thClass} w-[80px]`}>Rate MT/D</th>
                <th className={`${thClass} w-[70px]`}>Terms</th>
                <th className={`${thClass} w-[60px]`}>Turn(d)</th>
                <th className={`${thClass} w-[60px]`}>Freight</th>
                <th className={`${thClass} w-[60px]`}>Adcom%</th>
                <th className={`${thClass} w-[60px]`}>Other%</th>
                <th className={`${thClass} w-[65px]`}>Broker%</th>
                <th className={`${thClass} w-[105px]`}>Counter Party</th>
                <th className={`${thClass} w-[90px]`}>Cargo Name</th>
                <th className={`${thClass} w-[90px]`}>Stow Factor</th>
                <th className={`${thClass} w-[24px] max-w-[24px]`}></th>
              </tr>
            </thead>
            <tbody>
              {portOperations.map((item, index) => {
                const {
                  operation = "",
                  port = "",
                  cargoQty = "",
                  tolerance = "",
                  rate = "",
                  terms = "",
                  turnTime = "",
                  freight = "",
                  min = 0,
                  max = 0,
                  adcom = "",
                  other = "",
                  brokerage = "",
                  account = "",
                  cargo = "",
                  stowFactor = "",
                } = item || {};

                const firstLoadingIndex = portOperations.findIndex(
                  (op) => op?.operation?.toLowerCase() === "loading"
                );
                const disableForSingleFreight =
                  (freightMode === "Single Freight" && index !== firstLoadingIndex) || false;

                if (
                  item?.operation?.toLowerCase() !== "loading" &&
                  item?.operation?.toLowerCase() !== "discharging"
                ) {
                  return null;
                }

                return (
                  <tr className="hover:bg-[#ffffcc]/60 group" key={index}>
                    <td className={`${tdClass} w-[26px] min-w-[26px] text-center bg-slate-50`}>
                      <div className="flex items-center justify-center w-full h-[22px]">
                        <BiPlusCircle
                          size={14}
                          data-spotlight="add-port-btn"
                          className="cursor-pointer text-blue-600 hover:text-blue-800"
                          onClick={() => {
                            onAddPortOperations(index);
                            handleTourAction("add-ports");
                          }}
                        />
                      </div>
                    </td>

                    <td className={tdClass}>
                      <div className="relative">
                        <select
                          disabled={index < 1}
                          value={operation}
                          name="operation"
                          className={`${inputClass} appearance-none pr-4${operation.toLowerCase() === "discharging" ? "!text-rose-700" : ""}`}
                          onChange={(e) => onChangePortOperation(index, e)}
                        >
                          <option value="Commence" disabled hidden>Commence</option>
                          <option value="Loading">Loading</option>
                          <option value="Discharging">Discharging</option>
                        </select>
                        <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-slate-500">
                          <LuChevronDown size={12} />
                        </span>
                      </div>
                    </td>

                    <td className={tdClass}>
                      <div
                        className={`${inputClass} flex items-center justify-between cursor-pointer ${item.needsValidation ? "bg-amber-100 text-amber-800" : ""}`}
                        onClick={() => onSelectPortsOfOperations(item, index)}
                        title={item.needsValidation ? "Port name could not be resolved automatically." : ""}
                      >
                        <span className="truncate block w-full">
                          {port || <span className="text-gray-400">Select Port</span>}
                        </span>
                        {item.needsValidation && <MdWarning className="text-amber-600 ml-1 flex-shrink-0" size={12} />}
                      </div>
                    </td>

                    {(showMinMax ? [min, max] : [cargoQty, tolerance]).map(
                      (val, i) => {
                        const isDisabled = showMinMax
                          ? queryPortDisabledFields?.[operation]?.[i === 0 ? "min" : "max"]
                          : queryPortDisabledFields?.[operation]?.[i === 0 ? "cargoQty" : "tolerance"];
                        return (
                          <td key={i} className={`${tdClass} ${isDisabled ? "bg-[repeating-linear-gradient(135deg,#e2e8f0_0,#e2e8f0_4px,#f8fafc_4px,#f8fafc_8px)]" : ""}`}>
                            <input
                              type="number"
                              onKeyDown={handleKeyDown}
                              value={val}
                              name={showMinMax ? (i === 0 ? "min" : "max") : (i === 0 ? "cargoQty" : "tolerance")}
                              disabled={isDisabled}
                              onChange={(e) => onChangePortOperation(index, e)}
                              className={inputClass}
                              placeholder="0"
                            />
                          </td>
                        );
                      },
                    )}

                    {[
                      { name: "rate", value: rate },
                      { name: "terms", value: terms },
                      { name: "turnTime", value: turnTime },
                      { name: "freight", value: freight },
                      { name: "adcom", value: adcom },
                      { name: "other", value: other },
                      { name: "brokerage", value: brokerage },
                    ].map((field, i) => {
                      const isDisabled =
                        queryPortDisabledFields?.[operation]?.[field.name] ||
                        (["freight", "adcom", "other", "brokerage"].includes(field.name) && disableForSingleFreight);

                      return field.name === "terms" ? (
                        <td key={i} className={`${tdClass} ${isDisabled ? "bg-[repeating-linear-gradient(135deg,#e2e8f0_0,#e2e8f0_4px,#f8fafc_4px,#f8fafc_8px)]" : ""}`}>
                          <div className="relative">
                            <select
                              name="terms"
                              value={terms}
                              disabled={isDisabled}
                              onChange={(e) => onChangePortOperation(index, e)}
                              className={`${inputClass} appearance-none pr-4`}
                            >
                              <option value="" disabled hidden>—</option>
                              {termsCoEfficientTable.map((item, idx) => (
                                <option key={idx} value={item.label}>{item.label}</option>
                              ))}
                            </select>
                            <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-slate-500">
                              <LuChevronDown size={12} />
                            </span>
                          </div>
                        </td>
                      ) : (
                        <td key={i} className={`${tdClass} ${isDisabled ? "bg-[repeating-linear-gradient(135deg,#e2e8f0_0,#e2e8f0_4px,#f8fafc_4px,#f8fafc_8px)]" : ""}`}>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={field.value}
                            name={field.name}
                            disabled={isDisabled}
                            onChange={(e) => onChangePortOperation(index, e)}
                            className={inputClass}
                            placeholder="0"
                          />
                        </td>
                      );
                    })}

                    {[
                      { name: "account", value: account },
                      { name: "cargo", value: cargo },
                      { name: "stowFactor", value: stowFactor, type: "number" },
                    ].map((field, i) => {
                      const isDisabled = queryPortDisabledFields?.[operation]?.[field.name];
                      return (
                        <td key={i} className={`${tdClass} ${isDisabled ? "bg-[repeating-linear-gradient(135deg,#e2e8f0_0,#e2e8f0_4px,#f8fafc_4px,#f8fafc_8px)]" : ""}`}>
                          <input
                            type={field.type || "text"}
                            value={field.value}
                            name={field.name}
                            disabled={isDisabled}
                            onChange={(e) => onChangePortOperation(index, e)}
                            className={inputClass}
                            placeholder="—"
                          />
                        </td>
                      );
                    })}

                    <td className={`${tdClass} w-[24px] max-w-[24px] text-center bg-slate-100`}>
                      <div className="flex items-center justify-center w-full h-[22px]">
                        {index > 1 && (
                          <FaRegTrashAlt
                            className="text-rose-500 cursor-pointer hover:text-rose-700"
                            size={12}
                            onClick={() => onDeletePortOperations(index)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {isCounterPartySelection && (
          <div className="mb-3 mt-1">
            <CounterPartySelection
              portList={portOperations}
              updateCounterPartyAtPort={updateCounterPartyAtPort}
              handleDemurrageChange={handleDemurrageChange}
              handleDespatchChange={handleDespatchChange}
              isMultiSelect={true}
            />
          </div>
        )}

        {/* Clean, Readable Configuration & Remarks Panels */}
        <div className="flex flex-col md:flex-row gap-6 bg-white items-stretch mt-4">
          <div className="flex flex-col gap-3 bg-white p-0 w-full md:w-[42%]">
            <h3 className="text-[11px] font-medium text-slate-900 capitalize tracking-wider leading-none py-0.5 border-l-4 border-l-[#313C4C] pl-2 m-0">
              Configuration
            </h3>

            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium text-slate-700 capitalize tracking-wider">
                Name
              </label>
              <input
                className="w-full text-[11px] px-2 py-0.5 border border-slate-300 rounded-none focus:border-slate-800 outline-none text-slate-900 bg-white shadow-2xs h-8"
                placeholder="e.g. Wheat Cargo Q1"
                value={name}
                name="name"
                onChange={onChangeHandler}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-700 capitalize tracking-wider">
                  Freight Mode
                </label>
                <select
                  value={freightMode || ""}
                  onChange={onChangeHandler}
                  name="freightMode"
                  className="w-full text-[11px] px-2 py-0.5 border border-slate-300 rounded-none focus:border-slate-800 outline-none text-slate-900 bg-white shadow-2xs h-8"
                >
                  <option value="Single Freight">Voyage (Single)</option>
                  <option value="Multiple Freight">Multiple Freight</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-700 capitalize tracking-wider">
                  Qty Option
                </label>
                <select
                  value={qtyMode || ""}
                  onChange={onChangeHandler}
                  name="qtyMode"
                  className="w-full text-[11px] px-2 py-0.5 border border-slate-300 rounded-none focus:border-slate-800 outline-none text-slate-900 bg-white shadow-2xs h-8"
                >
                  <option value="MOLOO">MOLOO</option>
                  <option value="MINMAX">MINMAX</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-700 capitalize tracking-wider">
                  Laycan Start
                </label>
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <KeyboardDateTimePicker
                    autoOk
                    inputVariant="outlined"
                    format="DD-MM-YY HH:mm"
                    placeholder="dd-mm-yy hh:mm"
                    value={laycanStartDate || null}
                    onChange={(date) => onChangeDate("laycanStartDate", date)}
                    className="custom-date-time-picker w-full bg-white !h-8 !m-0 [&_.MuiOutlinedInput-root]:!h-8 [&_.MuiOutlinedInput-root]:box-border [&_.MuiOutlinedInput-root]:rounded-none [&_.MuiOutlinedInput-input]:p-1 [&_.MuiOutlinedInput-notchedOutline]:border-slate-300 [&_.MuiOutlinedInput-adornedEnd]:pr-1"
                    size="small"
                    InputProps={{ style: { fontSize: "11px", padding: 0 } }}
                    KeyboardButtonProps={{ style: { padding: 1 } }}
                    disableMaskedInput={true}
                  />
                </MuiPickersUtilsProvider>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-700 capitalize tracking-wider">
                  Laycan End
                </label>
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <KeyboardDateTimePicker
                    autoOk
                    inputVariant="outlined"
                    format="DD-MM-YY HH:mm"
                    placeholder="dd-mm-yy hh:mm"
                    value={laycanEndDate || null}
                    onChange={(date) => onChangeDate("laycanEndDate", date)}
                    className="custom-date-time-picker w-full bg-white !h-8 !m-0 [&_.MuiOutlinedInput-root]:!h-8 [&_.MuiOutlinedInput-root]:box-border [&_.MuiOutlinedInput-root]:rounded-none [&_.MuiOutlinedInput-input]:p-1 [&_.MuiOutlinedInput-notchedOutline]:border-slate-300 [&_.MuiOutlinedInput-adornedEnd]:pr-1"
                    size="small"
                    InputProps={{ style: { fontSize: "11px", padding: 0 } }}
                    KeyboardButtonProps={{ style: { padding: 1 } }}
                    disableMaskedInput={true}
                    refuse={/[^\d\s-:]/g}
                  />
                </MuiPickersUtilsProvider>
              </div>
            </div>

            <div className="flex gap-4 mt-1.5">
              {refreshCanalOptions.map((item, index) => {
                return (
                  <div key={index} className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      id={item.value}
                      name={item.value}
                      checked={query[item.value]}
                      onChange={onChangeHandler}
                      className="h-3.5 w-3.5 border-slate-300 rounded cursor-pointer"
                    />
                    <label
                      htmlFor={item.value}
                      className="cursor-pointer text-[11px] font-medium text-slate-700 leading-none"
                    >
                      {item.label}
                    </label>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col gap-0.5 mt-0">
              <div className="flex justify-between items-center mb-0.5">
                <label className="text-[11px] font-medium text-slate-800 capitalize tracking-wider">
                  Attachments
                </label>
                {(mediaTypes.images.length > 0 ||
                  mediaTypes.videos.length > 0 ||
                  mediaTypes.documents.length > 0) && (
                    <button
                      onClick={handleOpen}
                      className="text-[11px] flex items-center gap-1 text-slate-800 border-none font-medium hover:underline bg-transparent p-0 m-0 leading-none"
                    >
                      <MdLink size={12} /> View Saved ({mediaTypes.images.length + mediaTypes.videos.length + mediaTypes.documents.length})
                    </button>
                  )}
              </div>

              <div
                data-spotlight="upload-attachment-btn"
                className="border border-dashed border-slate-400 bg-white rounded-none flex justify-center items-center cursor-pointer hover:bg-slate-50 transition-colors py-1.5"
                onClick={() => {
                  if (!loader) {
                    fileInputRef.current.click();
                    handleTourAction("upload-attachment");
                  }
                }}
              >
                <div className="flex items-center gap-1.5">
                  <MdOutlineFileUpload
                    size={14}
                    className={loader ? "text-slate-400" : "text-slate-700"}
                  />
                  <span className="text-[11px] font-medium text-slate-600">
                    {loader ? "Uploading..." : "Click or drop to upload files"}
                  </span>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </div>

              {AttachmentFiles.length > 0 && (
                <div className="mt-1 max-h-[100px] overflow-y-auto border border-slate-200 rounded-none">
                  {AttachmentFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center px-2 py-1 text-[11px] bg-white border-b border-slate-100 last:border-0"
                    >
                      <span className="truncate text-slate-600 w-[90%]">
                        {file.name}
                      </span>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        disabled={loader}
                        className="text-slate-400 hover:text-rose-500 bg-transparent border-none p-0 m-0"
                      >
                        <MdClose size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 bg-white p-0 w-full md:w-[58%]">
            <h3 className="text-[11px] font-medium text-slate-900 capitalize tracking-wider leading-none py-0.5 border-l-4 border-l-[#313C4C] pl-2 m-0">
              Remarks / Notes
            </h3>
            <textarea
              className="w-full flex-1 min-h-[185px] p-2 text-[11px] border border-slate-300 rounded-none focus:border-slate-800 outline-none text-slate-800 bg-white resize-none shadow-2xs leading-relaxed"
              placeholder="Enter internal notes, special instructions, or terms here..."
              value={message}
              name="message"
              onChange={onChangeHandler}
            />
          </div>
        </div>

        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          className={classes.root}
        >
          <div className="flex justify-between items-center px-2 py-1.5 border-b border-slate-300 bg-slate-100">
            <span className="text-[11px] font-medium text-[#313C4C]">
              Attached Media Review
            </span>
            <button
              onClick={handleClose}
              className="text-slate-500 hover:text-slate-800 focus:outline-none bg-transparent p-0"
            >
              <MdClose size={16} />
            </button>
          </div>
          <DialogContent
            className={`${classes.dialogContent} p-2 bg-white`}
          ></DialogContent>
        </Dialog>

        <CargoAIModal
          open={getOpenCreateAI}
          handleClose={() => setOpenCreateAI(false)}
          query={query}
          portOperations={portOperations}
          setQuery={setQuery}
          setPortOperations={setPortOperations}
        />

        <UploadPDFModal
          open={getOpenUploadPDF}
          handleClose={() => {
            setOpenUploadPDF(false);
            handleActionComplete("upload-pdf");
          }}
          setPortOperations={setPortOperations}
          setQuery={setQuery}
          query={query}
        />

        <CustomShowModal
          open={portModal}
          handleClose={onClosePortModal}
          maxWidth="xs"
          disableClose
          title={`Change ${selectedPort.operation} Port`}
        >
          <AddPortQueryModal
            selectedPort={selectedPort}
            setSelectedPort={setSelectedPort}
            onSavePortOperationsOfQuery={onSavePortOperationsOfQuery}
          />
        </CustomShowModal>
      </div>
    </Fragment>
  );
});

export default AddNewOrder;